/**
 * The visitor badge photo the printer falls back to when no photo was captured.
 *
 * Manual check-in completes with no photo at all — nothing scans the ID, nobody poses for the camera.
 * The badge must still overwrite the label template's photo object, because the Brother template
 * (`VisitorTemplateHorizontal.lbx`) carries a stock face as its placeholder. The bridge's BPAC printer
 * writes whatever base64 it is given to a temp file and hands the path to that object; an empty string
 * produces a zero-byte file which b-PAC rejects by RETURNING false, and nothing checks that return. The
 * object then keeps the placeholder, and the dummy photo prints on the visitor's badge.
 *
 * Sending a real blank image guarantees the object is replaced, so the placeholder cannot reach the
 * printer. White rather than black: white pixels burn nothing on thermal media, so the photo box reads
 * as deliberately empty space instead of a solid block that looks like a print fault.
 */

let cached: string | undefined;

/**
 * A solid-white square image, base64-encoded, sized for nothing in particular — the printer scales it
 * into the template's photo frame.
 *
 * BMP because that is the format the bridge is already proven to accept: the document scanner's own
 * photo arrives as BMP, and the bridge writes the decoded bytes to a temp file with NO extension, so
 * b-PAC identifies the image from its content alone. Built once and cached.
 */
export function getBlankBadgePhotoBase64(): string {
    if (cached === undefined)
        cached = buildWhiteBmpBase64(32, 32);

    return cached;
}

/** Builds an uncompressed 24-bit all-white BMP. */
export function buildWhiteBmpBase64(width: number, height: number): string {
    const rowStride = (width * 3 + 3) & ~3;     // 24-bit rows are padded to a 4-byte boundary
    const pixelBytes = rowStride * height;
    const headerBytes = 54;                     // 14-byte BITMAPFILEHEADER + 40-byte BITMAPINFOHEADER

    const bytes = new Uint8Array(headerBytes + pixelBytes);
    const view = new DataView(bytes.buffer);

    // BITMAPFILEHEADER
    bytes[0] = 0x42;                            // 'B'
    bytes[1] = 0x4D;                            // 'M'
    view.setUint32(2, bytes.length, true);      // file size
    view.setUint32(10, headerBytes, true);      // offset to the pixel data

    // BITMAPINFOHEADER
    view.setUint32(14, 40, true);               // this header's size
    view.setInt32(18, width, true);
    view.setInt32(22, height, true);
    view.setUint16(26, 1, true);                // colour planes
    view.setUint16(28, 24, true);               // bits per pixel
    view.setUint32(34, pixelBytes, true);       // pixel data size

    // Every pixel white. The row padding bytes are part of this fill and are ignored by readers.
    bytes.fill(0xFF, headerBytes);

    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);

    return btoa(binary);
}
